// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useEffect, useMemo } from 'react';
import { stringify } from 'query-string';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';
import { useForm } from 'react-hook-form';

import useFetch from 'Hooks/useFetch';
import useAsyncError from 'Hooks/useAsyncError';
import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import useAnalytics from 'Hooks/useAnalytics';

import UsersComponent from 'Components/Users/Users';
import { API_USERS, EMAIL_REGEX } from 'Constants';

const defaultSortId = 'added-desc';

const Users = memo(() => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [isActionsOpen, setIsActionOpen] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [edit, setEdit] = useState('');
  const [deleteUserIdLoading, setDeleteUserIdLoading] = useState(null);
  const [emailUserIdLoading, setEmailUserIdLoading] = useState(null);
  const [errors, setErrors] = useState([]);

  const throwError = useAsyncError();
  const fetcher = useFetch();
  const { t } = useTranslation();
  const { location, query, push } = useRouter();
  const { data: { email, loggedIn } } = useUser();
  const analytics = useAnalytics();

  const languageOptions = useMemo(() => [
    { id: 'en', label: t('common.languages.english') },
    { id: 'ro', label: t('common.languages.romanian') },
  ], []);

  const statusOptions = useMemo(() => [
    { id: 'active', label: t('common.active') },
    { id: 'inactive', label: t('common.inactive') },
  ], []);

  const removedOptions = useMemo(() => [
    { id: 'removed', label: t('common.deleted') },
    { id: 'notRemoved', label: t('common.notDeleted') },
  ], []);

  const userRoleOptions = useMemo(() => [
    { id: '3', label: t('orders.roles.resellerUser') },
    { id: '8', label: t('orders.roles.visitor') },
    { id: '2', label: t('orders.roles.resellerAdmin'), disabled: true },
  ], []);

  const defaultValues = useMemo(() => ({
    firstName: '',
    lastName: '',
    language: null,
    status: null,
    email: '',
    password: '',
    confirmPassword: '',
    removed: null,
    userRole: null,
  }), []);

  const {
    formState: { errors: rhfErrors },
    register,
    control,
    reset,
    watch,
    getValues,
    setValue,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });

  const password = watch('password');
  const selectedLanguage = watch('language');
  const selectedStatus = watch('status');
  const selectedRemoved = watch('removed');
  const selectedUserRole = watch('userRole');
  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });
  const pattern = value => ({ value, message: t('errors.filedInvalidFormat') });

  const validators = {
    firstName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(100),
    },
    lastName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(100),
    },
    language: {
      required,
    },
    status: {
      required,
    },
    email: {
      required,
      maxLength: maxLength(50),
      pattern: pattern(EMAIL_REGEX),
    },
    password: {
      minLength: minLength(8),
      maxLength: maxLength(50),
      pattern: pattern(/[^\w\d]*(([0-9]+.*[A-Za-z]+.*)|[A-Za-z]+.*([0-9]+.*))/),
    },
    confirmPassword: {
      minLength: minLength(8),
      maxLength: maxLength(50),
      pattern: pattern(/[^\w\d]*(([0-9]+.*[A-Za-z]+.*)|[A-Za-z]+.*([0-9]+.*))/),
      validate: value => (value !== (password || '')) ? t('errors.passwordsDoNotMatch') : true,
    },
    removed: {
      required,
    },
    userRole: {
      required,
    },
  };

  const sortOptions = useMemo(() => [
    { id: 'user_id-asc', label: `${t('pages.id')} ${t('orders.ascending')}` },
    { id: 'user_id-desc', label: `${t('pages.id')} ${t('orders.descending')}` },
    { id: 'email-asc', label: `${t('pages.email')} ${t('orders.ascending')}` },
    { id: 'email-desc', label: `${t('pages.email')} ${t('orders.descending')}` },
    { id: 'first_name-asc', label: `${t('pages.firstName')} ${t('orders.ascending')}` },
    { id: 'first_name-desc', label: `${t('pages.firstName')} ${t('orders.descending')}` },
    { id: 'last_name-asc', label: `${t('pages.lastName')} ${t('orders.ascending')}` },
    { id: 'last_name-desc', label: `${t('pages.lastName')} ${t('orders.descending')}` },
    { id: 'added-asc', label: `${t('pages.added')} ${t('orders.ascending')}` },
    { id: 'added-desc', label: `${t('pages.added')} ${t('orders.descending')}` },
    { id: 'user_role-asc', label: `${t('pages.userRole')} ${t('orders.ascending')}` },
    { id: 'user_role-desc', label: `${t('pages.userRole')} ${t('orders.descending')}` },
  ], []);

  const defaultSort = sortOptions[9];
  const currentSortId = query?.order_by
    ? `${ query.order_by }-${ query.order_direction }`
    : defaultSortId;
  const [currentSort, setSort] = useState(
    currentSortId !== defaultSort.id
      ? sortOptions.find(item => item.id === currentSortId)
      : defaultSort
  );

  useEffect(() => {
    // check if query has sort
    if (Object.keys(omit(query || {}, ['deleted-users', 'user-role', 'user-search', 'page'])).length) {
      const { order_by: orderBy, order_direction: orderDirection } = query;
      setSort(sortOptions.find(item => item.id === `${ orderBy }-${ orderDirection }`));
    }
  }, []);

  const onMobileOrderChange = sort => {
    const [orderBy, orderDirection] = sort.split('-');
    analytics.track('sort_users', { order_by: orderBy, order_direction: orderDirection });

    if (orderDirection === 'none' || sort === defaultSortId) {
      setSort(sortOptions.find(item => item.id === defaultSortId));
      push({
        location: location.pathname,
        search: stringify(omit(query || {}, ['order_by', 'order_direction']))
      });
      return;
    }

    setSort(sortOptions.find(item => item.id === sort));

    const queryParams = {
      ...(omit(query || {}, ['order_by', 'order_direction', 'page'])),
      order_by: orderBy,
      order_direction: orderDirection
    };

    push({
      location: location.pathname,
      search: stringify(queryParams)
    });
  };

  const getUsers = () => {
    setUsersLoading(true);

    let path = API_USERS;

    if (query) {
      path += `?${ stringify({ ...query }) }`;
    }

    fetcher(path).then(res => {
      if (res.length === 0) {
        setUsers([]);
        setPagination(null);
        return;
      }

      const {
        users: usersResponse,
        ...paginationResponse
      } = res;

      const mappedUsers = usersResponse.map(user => ({
        ...user,
        id: user?.userId,
        actions: {
          userId: user?.userId,
          status: user?.status,
          removed: user?.removed,
        }
      }));

      setUsers(mappedUsers);
      setPagination(paginationResponse);
    }).catch(err => throwError(err))
      .finally(() => setUsersLoading(false));
  };

  // Get users on page load
  useEffect(() => {
    if (loggedIn) {
      getUsers();
    }

    // if (Object.keys(query || {}).length === 0 && !loggedIn) {
    //   push({
    //     location: location.pathname,
    //     search: ''
    //   });
    // }
  }, [location.pathname, query, email, loggedIn]);

  const handleAddUser = value => {
    analytics.track('toggle_add_user');
    reset(defaultValues);
    setEdit('');
    setIsActionOpen(value);
  };

  const handleEditUser = userId => {
    analytics.track('edit_user');
    setEdit(userId);
    setIsActionOpen(true);
    const user = users.find(u => u.userId === userId);

    if (!user) return;

    const {
      firstName,
      lastName,
      language,
      status,
      email: userEmail,
      password: userPassword,
      confirmPassword,
      removed,
      userRole,
    } = user;

    const languageOption = languageOptions.find(item => item.id === language);
    const statusOption = status ? statusOptions[0] : statusOptions[1];
    const removedOption = removed ? removedOptions[0] : removedOptions[1];
    const userRoleOption = userRoleOptions.find(item => Number(item.id || 0) === Number(userRole?.id || 0));

    setTimeout(() => {
      reset({
        firstName,
        lastName,
        language: languageOption,
        status: statusOption,
        email: userEmail,
        password: userPassword,
        confirmPassword,
        removed: removedOption,
        userRole: userRoleOption,
      });
    }, 0);
  };

  const handleDeleteUser = userId => {
    analytics.track('delete_user');
    setDeleteUserIdLoading(userId);
    fetcher(`${ API_USERS }/${ userId }`, { method: 'DELETE' })
      .then(() => {
        getUsers();
        setErrors([{ message: t('orders.userDeleted'), type: 'success' }]);
      })
      .catch(() => {
        setErrors([{ message: t('orders.userDeletedError'), type: 'error' }]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .finally(() => {
        setDeleteUserIdLoading('');
      });
  };

  const handleSendEmail = userId => {
    analytics.track('send_email');
    setEmailUserIdLoading(userId);
    fetcher(`${ API_USERS }/${ userId }/activation-email`, { method: 'POST' })
      .then(() => {
        setErrors([{ message: t('orders.emailSent'), type: 'success' }]);
      })
      .catch(() => {
        setErrors([{ message: t('orders.emailSentError'), type: 'error' }]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }).finally(() => {
        setEmailUserIdLoading('');
      });
  };

  const handleSubmit = async () => {
    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    const optionalOnAdd = ['status', 'password', 'confirmPassword', 'removed'];

    if (rhfErrorsKeys.length && (
      (!edit && !rhfErrorsKeys.every(element => optionalOnAdd.includes(element)))
      || edit
    )) {
      // Stop execution if any errors or edit is false and other fields besides (status, password, confirmPassword and removed) have error
      return;
    }

    const {
      firstName,
      lastName,
      language,
      status,
      email: userEmail,
      password: userPassword,
      confirmPassword,
      removed,
      userRole,
    } = getValues();

    const data = {
      firstName,
      lastName,
      language: language?.id,
      status: status?.id === 'active' ? 1 : 0,
      email: userEmail,
      password: { first: userPassword, second: confirmPassword },
      removed: removed?.id === 'removed' ? 1 : 0,
      userRole: userRole?.id,
    };

    setIsRequestPending(true);

    let path = API_USERS;
    let method = 'POST';

    if (edit) {
      path = `${API_USERS}/${edit}`;
      method = 'PUT';
      data.userId = edit;
    }

    await fetcher(path, {
      method,
      body: JSON.stringify(data),
    }).then(() => {
      getUsers();
      analytics.track(edit ? 'edit_user' : 'add_user');

      setIsActionOpen(false);
      reset(defaultValues);
    }).catch(err => setErrors([{ message: err.info.errors.map(i => i?.message).join('\r\n'), type: 'error' }]))
      .finally(() => {
        setIsRequestPending(false);
      });
  };

  return (
    <UsersComponent
      users={ users }
      usersLoading={ usersLoading }
      pagination={ pagination }
      isActionsOpen={ isActionsOpen }
      handleSubmit={ handleSubmit }
      isRequestPending={ isRequestPending }
      handleAddUser={ handleAddUser }
      handleEditUser={ handleEditUser }
      onDeleteUser={ handleDeleteUser }
      deleteUserIdLoading={ deleteUserIdLoading }
      handleSendEmail={ handleSendEmail }
      emailUserIdLoading={ emailUserIdLoading }
      sortOptions={ sortOptions }
      defaultSortId={ defaultSortId }
      currentSort={ currentSort }
      handleMobileOrderChange={ onMobileOrderChange }
      errors={ errors }
      edit={ edit }
      validators={ validators }
      register={ register }
      rhfErrors={ rhfErrors }
      setValue={ setValue }
      control={ control }
      selectedLanguage={ selectedLanguage }
      languageOptions={ languageOptions }
      selectedStatus={ selectedStatus }
      statusOptions={ statusOptions }
      selectedRemoved={ selectedRemoved }
      removedOptions={ removedOptions }
      userRoleOptions={ userRoleOptions }
      selectedUserRole={ selectedUserRole }
    />
  );
});

Users.displayName = 'UsersContainer';

export default Users;
